import { PAL } from "./palette";
import { drawText, drawTextC } from "./font";
import { colorsActive } from "./options";
import { TEAM_COLORS, NO_TEAM_RIVAL, type TeamColor } from "../shared/teamcolors";

export interface PodiumEntry { name: string; time: string; pts: number; team?: string; tc?: number }

// colour mode: medal blocks, sky, suits in the team colour, multicoloured confetti
const MEDAL: TeamColor[] = [
  { name: "GOLD", dark: "#b88a10", light: "#f4d440" },
  { name: "SILVER", dark: "#8892a0", light: "#d0d8e0" },
  { name: "BRONZE", dark: "#8a5428", light: "#d08848" },
];
const SKY = ["#74b4ee", "#88c0f2", "#9ccef6", "#b0dafa"];
const CONFETTI = ["#f0604a", "#f4d440", "#6f9ae6", "#6ccc60", "#f294bc", "#ffffff"];

export const PODIUM_FRAMES = 540; // 9 s, then the full results table takes over

const BASE = 112;
// [centre x, block height, enter frame, helmet shade, body shade] per place (index 0 = winner)
const SLOT = [
  { cx: 80, h: 30, at: 60, helmet: 3, body: 2 },
  { cx: 38, h: 22, at: 36, helmet: 2, body: 3 },
  { cx: 122, h: 14, at: 12, helmet: 3, body: 1 },
];

function rect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x), Math.round(y), w, h);
}

/** A 10px-tall pilot: helmet, visor, torso, arms (up = waving/celebrating) */
function drawPilot(ctx: CanvasRenderingContext2D, cx: number, feet: number, t: number, place: number, helmet: string, body: string, suit: TeamColor | null = null) {
  const OUT = suit ? suit.dark : PAL[3];
  const wave = Math.floor(t / 8) % 2;
  const up = place === 0 ? 2 : place === 1 ? 1 : 0;
  const x = cx - 4;
  const y = feet - 10;
  // legs
  rect(ctx, x + 1, feet - 2, 2, 2, suit ? "#1c2830" : PAL[3]);
  rect(ctx, x + 5, feet - 2, 2, 2, suit ? "#1c2830" : PAL[3]);
  // torso
  rect(ctx, x, y + 5, 8, 4, OUT);
  rect(ctx, x + 1, y + 6, 6, 2, suit ? suit.light : body);
  // arms
  const arm = (side: -1 | 1, raised: boolean, alt: boolean) => {
    const ax = side < 0 ? x - 2 : x + 8;
    if (raised) {
      rect(ctx, ax, y + (alt ? 0 : 1), 2, 6, OUT);
    } else rect(ctx, ax, y + 6, 2, 3, OUT);
  };
  arm(-1, up >= 1, wave === 1);
  arm(1, up >= 2, wave === 0);
  // helmet + visor
  rect(ctx, x + 1, y, 6, 5, suit ? suit.light : PAL[3]);
  if (suit) rect(ctx, x + 1, y, 6, 1, OUT);
  rect(ctx, x + 2, y + 1, 4, 3, suit ? "#d8f0ff" : helmet === PAL[3] ? PAL[1] : PAL[0]);
  rect(ctx, x + 2, y + 2, 4, 1, suit ? "#1c2830" : PAL[3]);
  if (place === 0) { // trophy raised in the right hand
    const ty = y - 7 + (wave ? 0 : 1);
    rect(ctx, x + 8, ty, 5, 3, suit ? MEDAL[0].dark : PAL[3]);
    rect(ctx, x + 9, ty, 3, 2, suit ? MEDAL[0].light : PAL[1]);
    rect(ctx, x + 10, ty + 3, 1, 2, suit ? MEDAL[0].dark : PAL[3]);
    rect(ctx, x + 9, ty + 5, 3, 1, suit ? MEDAL[0].dark : PAL[3]);
  }
}

function confetti(ctx: CanvasRenderingContext2D, t: number) {
  const start = SLOT[0].at + 20;
  if (t < start) return;
  const age = t - start;
  for (let i = 0; i < 36; i++) {
    const h = (i * 2654435761) >>> 0;
    const x = (h % 150) + 5;
    const speed = 0.5 + ((h >>> 8) % 10) / 10;
    const y = ((age * speed + ((h >>> 12) % 100)) % 112);
    const sway = Math.sin((age + i * 7) / 9) * 3;
    rect(ctx, x + sway, y + 8, 2, 2, colorsActive() ? CONFETTI[i % CONFETTI.length] : PAL[(i % 2) + 1]);
  }
}

/** Animated podium for the top 3 of an online race. `t` counts frames since the results arrived. */
export function drawPodium(ctx: CanvasRenderingContext2D, t: number, title: string, top: PodiumEntry[]) {
  const col = colorsActive();
  if (col) SKY.forEach((c, i) => rect(ctx, 0, i * 28, 160, 28, c));
  drawTextC(ctx, "PODIUM", 80, 3, PAL[3], 2);
  drawTextC(ctx, title, 80, 15, PAL[2]);
  confetti(ctx, t);
  // ground
  rect(ctx, 0, BASE, 160, 1, PAL[3]);
  for (let place = 0; place < Math.min(3, top.length); place++) {
    const s = SLOT[place];
    const e = top[place];
    const grow = Math.min(1, Math.max(0, (t - s.at + 10) / 14));
    const h = Math.round(s.h * grow);
    // block
    rect(ctx, s.cx - 19, BASE - h, 38, h, PAL[3]);
    if (col && h > 2) rect(ctx, s.cx - 18, BASE - h + 1, 36, h - 1, MEDAL[place].light);
    if (col && h > 6) rect(ctx, s.cx - 18, BASE - 3, 36, 2, MEDAL[place].dark);
    if (!col && h > 2) rect(ctx, s.cx - 18, BASE - h + 1, 36, 1, PAL[2]);
    if (h === 0) continue;
    if (h >= s.h) drawTextC(ctx, String(place + 1), s.cx, BASE - s.h + 3, col ? PAL[3] : PAL[0], 2);
    const sinceIn = t - s.at;
    if (sinceIn < 0) continue;
    // drop in, then a small celebratory hop for the winner
    const fall = Math.max(0, 1 - sinceIn / 16);
    const hop = sinceIn > 30 && place === 0 ? Math.abs(Math.sin(sinceIn / 7)) * 3 : 0;
    const feet = BASE - s.h - Math.round(fall * fall * 70) - Math.round(hop);
    drawPilot(ctx, s.cx, feet, sinceIn, place, PAL[s.helmet], PAL[s.body], col ? (e.tc !== undefined ? TEAM_COLORS[e.tc] ?? NO_TEAM_RIVAL : NO_TEAM_RIVAL) : null);
    if (sinceIn > 16) {
      const name = e.name.slice(0, 9);
      drawTextC(ctx, name, s.cx, BASE - s.h - 22 - (place === 0 ? 8 : 0), PAL[3]);
      if (e.team) drawTextC(ctx, e.team.slice(0, 4), s.cx, BASE - s.h - 16 - (place === 0 ? 8 : 0), PAL[2]); // the pilot's team tag, just under the name
      drawTextC(ctx, e.time, s.cx, BASE + 4, PAL[3]);
      if (e.pts > 0) drawTextC(ctx, `${e.pts}P`, s.cx, BASE + 11, PAL[2]);
    }
  }
  if (t > 90) drawTextC(ctx, t % 60 < 40 ? "PRESS START" : "", 80, 134, PAL[2]);
}
